import React from 'react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { 
  Briefcase, 
  Calculator, 
  FileText, 
  Building2, 
  CreditCard, 
  TrendingUp,
  Users,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Star,
  Lock,
  FolderLock,
  FileCheck,
  PiggyBank,
  BarChart3,
  Layers,
  Target
} from 'lucide-react';

const ApplicationPage = () => {
  const allInOneFeatures = [
    "Recherche de missions freelance",
    "Facturation et devis conformes",
    "Compte bancaire synchronisé",
    "Kbis et documents officiels",
    "Bilans comptables",
    "Conventions et contrats",
    "Simulateurs de charges",
    "Optimisation fiscale",
    "Réseau de partenaires"
  ];

  const freemiumFeatures = [
    {
      icon: Briefcase,
      title: "Offres de missions",
      description: "Postulez à des offres de missions sélectionnées, créées par nos partenaires ESN"
    },
    {
      icon: FileText,
      title: "Guides experts",
      description: "Accédez à des guides complets sur la recherche de missions, les statuts d'entreprise, et plus"
    },
    {
      icon: Calculator,
      title: "Simulateurs gratuits",
      description: "15+ simulateurs pour calculer vos revenus, charges, impôts et optimiser votre situation"
    },
    {
      icon: Shield,
      title: "Veille juridique",
      description: "Restez informé des nouvelles lois, taxes et réglementations qui impactent les freelances"
    },
    {
      icon: TrendingUp,
      title: "Scénarios personnalisés",
      description: "Simulez différents scénarios : chômage, changement de statut, évolution d'activité"
    },
    {
      icon: Target,
      title: "Analyse de viabilité",
      description: "Testez la viabilité de votre projet en un clic avec notre analyse intelligente"
    }
  ];

  const proFeatures = [
    {
      icon: FolderLock,
      title: "Coffre-fort numérique",
      description: "Stockez tous vos documents : contrats partenaires, bilans comptables, conventions trésorier, Kbis..."
    },
    {
      icon: Users,
      title: "Réseau de partenaires",
      description: "Simulez les coûts avec nos partenaires vérifiés : comptables, banques, assurances, agences web, fournisseurs matériel informatique avec remise"
    },
    {
      icon: Building2,
      title: "Sélection partenaires",
      description: "Choisissez et travaillez directement avec les partenaires qui correspondent à vos besoins"
    },
    {
      icon: FileCheck,
      title: "Facturation conforme",
      description: "Éditez des factures et devis conformes à la nouvelle législation française"
    },
    {
      icon: CreditCard,
      title: "Synchronisation bancaire",
      description: "Connectez votre compte bancaire pour un suivi automatique de votre trésorerie"
    },
    {
      icon: BarChart3,
      title: "Simulations avancées",
      description: "Analysez vos scénarios : gains, dépenses, salaire, dividendes, charges, impôts, TVA par statut"
    }
  ];

  const partners = [
    "ESN partenaires",
    "Cabinets comptables",
    "Banques",
    "Assurances professionnelles",
    "Agences web",
    "Juristes spécialisés",
    "Fournisseurs matériel informatique (remises négociées)"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-24">
      <SEO
        title="Application tout-en-un pour freelances"
        description="Bozzam regroupe tout pour gérer votre activité freelance : missions ESN, facturation, coffre-fort numérique, synchronisation bancaire, simulateurs de charges."
        path="/application"
      />
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-black text-gray-900 mb-6">
            L'application <span className="text-blue-600">tout-en-un</span> des freelances
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Fini les multiples plateformes ! Bozzam regroupe tout ce dont vous avez besoin pour gérer votre activité : 
            missions, facturation, comptabilité, documents, simulation de viabilité... tout est centralisé.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold"
              onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}
            >
              Accéder à l'application
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-gray-300 px-8 py-4 rounded-xl font-semibold"
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
            >
              Découvrir les fonctionnalités
            </Button>
          </div>
        </div>

        {/* All-in-One Section */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 text-white mb-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Layers className="h-4 w-4" />
              Tout-en-un
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Une seule plateforme, zéro dispersion
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Au lieu de jongler entre plusieurs outils et plateformes, retrouvez tout ce dont vous avez besoin pour gérer votre entreprise au même endroit.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {allInOneFeatures.map((feature, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <CheckCircle className="h-5 w-5 text-green-400 mx-auto mb-2" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Missions ESN Highlight */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-3xl p-8 md:p-12 text-white mb-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="h-6 w-6" />
                <span className="font-semibold">Missions Freelance</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Accédez à des missions exclusives
              </h2>
              <p className="text-blue-100 text-lg mb-6">
                Nos partenaires ESN publient régulièrement des offres de missions sélectionnées. 
                Postulez directement depuis l'application et trouvez votre prochaine mission.
              </p>
              <ul className="space-y-3">
                {["Missions vérifiées et qualifiées", "Partenaires ESN de confiance", "Processus de candidature simplifié", "Suivi de vos candidatures"].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-center">
                  <div className="text-5xl font-bold mb-2">100+</div>
                  <div className="text-blue-200">Missions disponibles</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Simulation & Viability Section */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-3xl p-8 md:p-12 text-white mb-20">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="h-6 w-6" />
                <span className="font-semibold">Simulation de viabilité</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Votre projet est-il viable ?
              </h2>
              <p className="text-emerald-100 text-lg mb-6">
                Notre simulateur analyse votre scénario complet : gains, dépenses, salaire, dividendes, charges, impôts et TVA. 
                En fonction de votre statut juridique, découvrez si votre projet est rentable.
              </p>
              <ul className="space-y-3">
                {[
                  "Analyse complète par statut (SASU, EURL, micro-entreprise...)",
                  "Simulation salaire vs dividendes",
                  "Calcul des charges sociales et fiscales",
                  "Projection de rentabilité (ventes, dépenses, bénéfices)",
                  "Recommandation du statut optimal pour votre trésorerie"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-yellow-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <PiggyBank className="h-16 w-16 mx-auto mb-4 text-yellow-300" />
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">Optimisez</div>
                  <div className="text-emerald-200">votre rentabilité</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Freemium */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Star className="h-4 w-4" />
              Version Freemium - Gratuite
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Commencez gratuitement
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Accédez à toutes les fonctionnalités essentielles pour lancer et gérer votre activité freelance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {freemiumFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Pro */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Lock className="h-4 w-4" />
              Version Pro - Bientôt disponible
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Passez au niveau supérieur
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Des outils avancés pour optimiser votre activité et centraliser tous vos documents
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {proFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 relative overflow-hidden"
              >
                <div className="absolute top-4 right-4">
                  <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded-full">
                    Pro
                  </span>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Coffre-fort highlight */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 md:p-12 border border-amber-200 mb-20">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 bg-amber-100 rounded-2xl flex items-center justify-center">
                <FolderLock className="h-12 w-12 text-amber-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Coffre-fort numérique sécurisé
              </h3>
              <p className="text-gray-600 text-lg mb-4">
                Retrouvez tous vos documents importants au même endroit : contrats signés avec vos partenaires, 
                bilans comptables, conventions trésorier, Kbis, statuts... Plus besoin de chercher partout !
              </p>
              <div className="flex flex-wrap gap-2">
                {["Contrats partenaires", "Bilans comptables", "Kbis", "Conventions", "Statuts", "Factures"].map((doc, i) => (
                  <span key={i} className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                    {doc}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Partners */}
        <div className="bg-gray-50 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Nos partenaires de confiance
            </h2>
            <p className="text-gray-600">
              Travaillez avec des professionnels vérifiés et adaptés à vos besoins
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {partners.map((partner, index) => (
              <div 
                key={index}
                className="bg-white px-6 py-3 rounded-full border border-gray-200 text-gray-700 font-medium"
              >
                {partner}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-3xl p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Prêt à simplifier votre vie de freelance ?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Une seule application pour tout gérer. Rejoignez les freelances qui ont choisi Bozzam.
          </p>
          <Button 
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-xl font-semibold"
            onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}
          >
            Commencer gratuitement
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ApplicationPage;
